(() => {
  const instances = new WeakMap();

  class ContactPage {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.toastTimer = 0;
      this.init();
    }

    init() {
      this.forms = [...this.root.querySelectorAll('[data-contact-form]')];
      this.bindCopy();
      this.bindDepartments();
      this.forms.forEach((form) => this.initForm(form));
      this.focusStatus();
    }

    initForm(form) {
      const message = form.querySelector('[data-contact-message]');
      const count = form.querySelector('[data-contact-count]');
      const subject = form.querySelector('[data-contact-subject]');
      const submit = form.querySelector('[data-contact-submit]');

      if (message && count) {
        const updateCount = () => {
          const max = Number(message.maxLength) || 0;
          count.textContent = `${message.value.length} / ${max}`;
        };
        message.addEventListener('input', updateCount, { signal: this.signal });
        updateCount();
      }

      if (subject) {
        const updateConditionalFields = () => {
          const selected = this.normalize(subject.value);
          this.root.querySelectorAll('[data-contact-conditional]').forEach((field) => {
            const raw = field.dataset.contactConditional?.trim();
            if (!raw) return;
            const matches = raw
              .split(/<br\s*\/?\s*>|\n|,/i)
              .map((value) => this.normalize(value))
              .filter(Boolean);
            const visible = matches.includes(selected);
            field.hidden = !visible;
            field.querySelectorAll('input,select,textarea').forEach((control) => {
              if (!control.dataset.originalRequired) {
                control.dataset.originalRequired = control.required ? 'true' : 'false';
              }
              control.disabled = !visible;
              control.required = visible && control.dataset.originalRequired === 'true';
            });
          });
        };
        subject.addEventListener('change', updateConditionalFields, { signal: this.signal });
        updateConditionalFields();
      }

      form.addEventListener('submit', (event) => {
        if (!form.checkValidity()) return;
        if (submit?.disabled) {
          event.preventDefault();
          return;
        }
        if (submit) {
          submit.disabled = true;
          const label = submit.querySelector('span');
          if (label) {
            label.dataset.originalText = label.textContent;
            label.textContent = submit.dataset.loadingLabel || 'Enviando...';
          }
        }
      }, { signal: this.signal });

      if (this.root.dataset.autoFocus === 'true' && !window.matchMedia('(pointer: coarse)').matches) {
        requestAnimationFrame(() => {
          const first = form.querySelector('input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled])');
          first?.focus({ preventScroll: true });
        });
      }
    }

    bindCopy() {
      this.root.querySelectorAll('[data-contact-copy]').forEach((button) => {
        button.addEventListener('click', async () => {
          const value = button.dataset.contactCopy || '';
          try {
            await navigator.clipboard.writeText(value);
          } catch (_) {
            const input = document.createElement('textarea');
            input.value = value;
            input.setAttribute('readonly', '');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
          }
          this.showToast(button.getAttribute('aria-label') ? `${button.getAttribute('aria-label')}: ${value}` : 'Copiado');
        }, { signal: this.signal });
      });
    }

    bindDepartments() {
      this.root.querySelectorAll('[data-contact-department]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const value = trigger.dataset.contactDepartment || '';
          const form = this.root.querySelector('[data-contact-form]');
          const subject = form?.querySelector('[data-contact-subject]');
          if (subject && value) {
            const option = [...subject.options].find((item) => this.normalize(item.value) === this.normalize(value));
            if (option) {
              subject.value = option.value;
              subject.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          requestAnimationFrame(() => form?.querySelector('[data-contact-message]')?.focus({ preventScroll: true }));
        }, { signal: this.signal });
      });
    }

    focusStatus() {
      const status = this.root.querySelector('[data-contact-success], [data-contact-error]');
      if (!status) return;
      requestAnimationFrame(() => {
        status.focus({ preventScroll: true });
        status.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      });
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    showToast(message) {
      const toast = this.root.querySelector('[data-contact-toast]');
      if (!toast) return;
      window.clearTimeout(this.toastTimer);
      toast.textContent = message;
      toast.classList.add('is-visible');
      this.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
    }

    destroy() {
      window.clearTimeout(this.toastTimer);
      this.controller.abort();
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll?.('[data-contact-section]').forEach((root) => {
      instances.get(root)?.destroy();
      const instance = new ContactPage(root);
      instances.set(root, instance);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-contact-section]').forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
